import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { format, parseISO } from 'date-fns';

import { useGetAvailableDetailedExercises } from '../../../../redux/api/exercisesAPI';
import {
  useGetTrainingExercisesDetailedQuery,
  useGetTrainingsByDay,
} from '../../../../redux/api/trainingsAPI';
import { TrainingStatus } from '../../../../shared/constants';
import { IExercise } from '../../../../types/interfaces/exercise';
import { orderBy, uniqBy } from '../../../../utils/collections/array-utils';

export function useStatisticsDay() {
  const { t } = useTranslation();
  const { day } = useParams<{ day: string }>();
  const dayString = useMemo(() => {
    if (!day) return '';
    try {
      const parsed = parseISO(day);
      if (isNaN(parsed.getTime())) return '';
      return format(parsed, 'dd-MM-yyyy');
    } catch {
      return '';
    }
  }, [day]);

  const {
    data: dayTrainings = [],
    isLoading: isLoadingTrainings,
    isFetching: isFetchingTrainings,
    isError: isDayTrainingsError,
    error: dayTrainingsError,
  } = useGetTrainingsByDay(
    {
      day: dayString,
      statuses: [TrainingStatus.started, TrainingStatus.finished],
    },
    { skip: !dayString },
  );

  const firstTrainingId = dayTrainings[0]?.id;

  const {
    data: availableDetailedExercises,
    isLoading: isLoadingAvailableDetailedExercises,
    isFetching: isFetchingAvailableDetailedExercises,
    isError: isAvailableDetailedExercisesError,
    error: availableDetailedExercisesError,
  } = useGetAvailableDetailedExercises();

  const {
    data: trainingExercisesDetailed,
    isError: isTrainingExercisesDetailedError,
    error: trainingExercisesDetailedError,
  } = useGetTrainingExercisesDetailedQuery(firstTrainingId, {
    skip: !firstTrainingId,
  });

  const exercises = useMemo(() => {
    if (
      isLoadingAvailableDetailedExercises ||
      isFetchingAvailableDetailedExercises
    ) {
      return undefined;
    }

    let result: IExercise[] = [];

    if (availableDetailedExercises) {
      result = [...availableDetailedExercises];
    }

    if (trainingExercisesDetailed) {
      result = [...result, ...trainingExercisesDetailed];
    }

    return result.length > 0
      ? orderBy(uniqBy(result, 'id'), 'name')
      : undefined;
  }, [
    availableDetailedExercises,
    trainingExercisesDetailed,
    isLoadingAvailableDetailedExercises,
    isFetchingAvailableDetailedExercises,
  ]);

  const formattedDate = useMemo(() => {
    if (!day) return '';
    try {
      const parsed = parseISO(day);
      if (isNaN(parsed.getTime())) return '';
      return format(parsed, 'dd.MM.yyyy');
    } catch {
      return '';
    }
  }, [day]);

  const pageTitle = useMemo(() => {
    if (!day) return t('statistics.dayStatistics');
    return `${t('statistics.dayStatistics')} - ${formattedDate}`;
  }, [day, formattedDate, t]);

  return {
    dayString,
    dayTrainings,
    exercises,
    isLoadingTrainings,
    isFetchingTrainings,
    pageTitle,
    isDayTrainingsError,
    dayTrainingsError,
    isAvailableDetailedExercisesError,
    availableDetailedExercisesError,
    isTrainingExercisesDetailedError,
    trainingExercisesDetailedError,
  };
}
